import { useCallback, useMemo } from "react";
import { useLocalView } from "@/hooks/useLocalView";
import type { LocalPage } from "@/lib/localRead";
import { tasksAmong, type TaskJson, type ViewSource } from "@/lib/views";
import { useTaskStore } from "@/stores/taskStore";

export interface LocalTasks {
  rows: Record<string, TaskJson>;
  // A row missing from the local copy is not proof of deletion (old finished work is not
  // kept); these ids are: an unsent op discards them, or a pull brought their tombstone.
  deleted: ReadonlySet<string>;
}

const ID_SEPARATOR = " ";

/**
 * The local copy of the given tasks, or null while the caller should read its own data
 * (see useLocalView).
 */
export function useLocalTasks(page: LocalPage, ids: readonly string[]): LocalTasks | null {
  const key = ids.join(ID_SEPARATOR);
  const stableIds = useMemo(() => (key ? key.split(ID_SEPARATOR) : []), [key]);
  const view = useCallback((source: ViewSource) => tasksAmong(source, stableIds), [stableIds]);
  const local = useLocalView(page, view);
  const stored = useTaskStore((s) => s.tasks);
  const pulledAt = useTaskStore((s) => s.pulledAt);

  return useMemo(() => {
    if (!local) return null;
    const deleted = new Set(local.discarded);
    for (const id of stableIds) {
      if (!local.rows[id] && !stored[id] && pulledAt[id] !== undefined) deleted.add(id);
    }
    return { rows: local.rows, deleted };
  }, [local, stored, pulledAt, stableIds]);
}
